import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { Badge } from "components/bootstrap";
import Icon from "components/common/Icon";
import { CELL_PADDING_HORIZONTAL } from "components/common/EntityDataTable/Constants";
const DEFAULT_COLLAPSED_COUNT = 1;
const EXPANDED_MAX_HEIGHT = 240;
const CollapsedRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.xs};
    min-width: 0;
  `
);
const CollapsedItem = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Expanded = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xs};
  `
);
const Items = styled.div(
  ({ theme, $layout, $scrollable }) => css`
    display: flex;
    min-width: 0;
    ${$layout === "stacked" ? css`
          flex-direction: column;
          align-items: flex-start;
          gap: ${theme.spacings.xxs};
        ` : css`
          flex-wrap: wrap;
          gap: ${theme.spacings.xs};
        `}
    ${$scrollable && css`
      max-height: ${EXPANDED_MAX_HEIGHT}px;
      overflow: hidden auto;

      /* Extends into the cell padding, so the scrollbar sits at the cell border. */
      margin-right: -${CELL_PADDING_HORIZONTAL}px;
      padding-right: ${CELL_PADDING_HORIZONTAL}px;
    `}
  `
);
const Item = styled.div(
  ({ $layout }) => css`
    min-width: 0;
    max-width: 100%;
    ${$layout === "stacked" && css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      /* With overflow: hidden the item may shrink below its content height, so a capped list would squeeze it. */
      flex-shrink: 0;
    `}
  `
);
const ToggleBadge = styled(Badge)`
  flex-shrink: 0;
  align-self: flex-start;
`;
const ExpandableCellList = ({
  items,
  renderItem,
  itemKey,
  itemTitle = void 0,
  collapsedCount = DEFAULT_COLLAPSED_COUNT,
  layout = "stacked"
}) => {
  const { t } = useTranslation("common");
  const [isExpanded, setIsExpanded] = useState(false);
  if (!(items == null ? void 0 : items.length)) {
    return null;
  }
  const hiddenCount = items.length - collapsedCount;
  const renderAllItems = (scrollable) => /* @__PURE__ */ React.createElement(Items, { $layout: layout, $scrollable: scrollable }, items.map((item) => /* @__PURE__ */ React.createElement(Item, { key: itemKey(item), $layout: layout, title: itemTitle == null ? void 0 : itemTitle(item) }, renderItem(item))));
  if (hiddenCount <= 0) {
    return renderAllItems(false);
  }
  const toggle = (e) => {
    e.stopPropagation();
    setIsExpanded((cur) => !cur);
  };
  if (!isExpanded) {
    const title = t("expandable_cell_list.show_more", { count: hiddenCount });
    return /* @__PURE__ */ React.createElement(CollapsedRow, null, items.slice(0, collapsedCount).map((item) => /* @__PURE__ */ React.createElement(CollapsedItem, { key: itemKey(item), title: itemTitle == null ? void 0 : itemTitle(item) }, renderItem(item))), /* @__PURE__ */ React.createElement(ToggleBadge, { color: "gray", size: "sm", onClick: toggle, title, "aria-label": title }, "+", hiddenCount, /* @__PURE__ */ React.createElement(Icon, { name: "keyboard_arrow_down", size: "sm" })));
  }
  return /* @__PURE__ */ React.createElement(Expanded, null, renderAllItems(true), /* @__PURE__ */ React.createElement(ToggleBadge, { color: "gray", size: "sm", onClick: toggle }, t("expandable_cell_list.show_less"), /* @__PURE__ */ React.createElement(Icon, { name: "keyboard_arrow_up", size: "sm" })));
};
export default ExpandableCellList;
